import ConditionalToolTip from "components/design/conditionalToolTip";
import { BackendError } from "types/utils/error";
import { DeleteModal } from "components/design/deleteModal";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form1099Fields } from "components/Form1099/Form1099Fields";
import { useModal } from "hooks/useModal";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { Padlock } from "components/icons/Padlock";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  useBulkUpdateVendorsMutation,
  useDeleteVendorMutation,
  useEditVendorsMutation,
} from "store/apis/vendors";
import { Vendor } from "types/Models/vendors";
import { EditVendorDetails } from "./VendorDetail/EditVendorDetails";
import { MarkAsNotRequired } from "./MarkAsNotRequired";
import { MarkAsRequired } from "./MarkAsRequired";
import { MarkVendorAsVoid } from "./MarkVendorAsVoid";

export const MerchantTableDropdown = ({
  openMarkAsVendor,
  merchantId,
  groupId,
  isManualMerchant,
  isVendor,
  showTransactions,
  expandedRow,
  vendorName,
  season,
  form1099Status,
  vendor,
  taskId,
  entityId,
  hasEmail,
  setAnimateVendors,
  selectedSeason,
}: {
  openMarkAsVendor: () => void;
  merchantId: string;
  groupId: string;
  isManualMerchant: boolean;
  isVendor: boolean;
  showTransactions: (vendorName: string | null) => void;
  expandedRow: string;
  vendorName: string;
  season: string;
  form1099Status: "NOT_STARTED" | "NOT_REQUIRED" | "PENDING" | "COMPLETED";
  vendor: Vendor;
  taskId?: string;
  entityId: string;
  hasEmail: boolean;
  setAnimateVendors: React.Dispatch<React.SetStateAction<string[]>>;
  selectedSeason?: string;
}) => {
  const { isCustomer, isCpa, isAdmin } = useRoleBasedView();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.EXPENSE
  );
  const [deleteMerchant, { isLoading: isDeleting }] = useDeleteVendorMutation();
  const { alertToast, successToast } = useToast();
  const markAsNotRequiredModal = useModal();
  const markAsRequiredModal = useModal();
  const deleteModal = useModal();
  const notRequiredModal = useModal();
  const editVendorModal = useModal();
  const markAsVoid = useModal();
  const edit1099Modal = useModal();

  const [editVendor, { isLoading: isEditing }] = useEditVendorsMutation();
  const [bulkUpdateVendors] = useBulkUpdateVendorsMutation();

  const markVendorFormNotRequired = async () => {
    try {
      await editVendor({
        groupId,
        uuid: merchantId,
        payload: { merchant_type: "NOT_REQUIRED", season },
      }).unwrap();
      notRequiredModal.close();
      successToast({ message: "Vendor has been updated" });
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const deleteVendor = async () => {
    try {
      await deleteMerchant({ merchantId, groupId }).unwrap();
      successToast({ message: "Vendor deleted successfully" });
      deleteModal.close();
    } catch (e: any) {
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const markAsRelatedParty = async () => {
    try {
      await bulkUpdateVendors({
        groupId,
        vendor_ids: [vendor.uuid],
        update_data: { is_related_party: !vendor.is_related_party },
      }).unwrap();
      successToast({
        message: vendor.is_related_party
          ? "Vendor unmarked as related party"
          : "Vendor marked as related party",
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const isForm1099Completed = form1099Status === "COMPLETED";
  const allowRequireTaxDocument = !isVendor && isCpa && !isForm1099Completed;
  const allowMark1099NotRequired =
    isCpa && form1099Status === "NOT_STARTED" && !isForm1099Completed;
  const allowSkipTaxDocument = isVendor && isCpa && !isForm1099Completed;
  const allowDeleteVendor =
    !isCustomer &&
    isManualMerchant &&
    !isForm1099Completed &&
    hasWritePermission;
  const allowMark1099Required = isCpa && form1099Status === "NOT_REQUIRED";
  const isVoided = vendor.filing_status === "VOIDED";
  const allowVoidFiling = isCpa && taskId && !isVoided;
  const showEdit1099 =
    isCpa && vendor?.form_1099_document?.recipients_document && !isVoided;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button type="button" customType="ghost_icon">
            <img src={ThreeDots} alt="Three dots" />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end">
            <Dropdown.Item
              onClick={hasWritePermission ? editVendorModal.open : undefined}
              disabled={!hasWritePermission}
              tag={!hasWritePermission ? <Padlock /> : undefined}
            >
              Edit vendor
            </Dropdown.Item>
            {showEdit1099 && (
              <Dropdown.Item onClick={edit1099Modal.open}>
                Amend 1099
              </Dropdown.Item>
            )}
            {allowRequireTaxDocument && (
              <ConditionalToolTip
                condition={
                  !season && "Filter by a season to enable this action."
                }
                side="left"
              >
                <Dropdown.Item onClick={openMarkAsVendor} disabled={!season}>
                  Mark W-Form required
                </Dropdown.Item>
              </ConditionalToolTip>
            )}
            {allowMark1099NotRequired && (
              <ConditionalToolTip
                condition={
                  !season && "Filter by a season to enable this action."
                }
                side="left"
              >
                <Dropdown.Item
                  onClick={markAsNotRequiredModal.open}
                  disabled={!season}
                >
                  Mark 1099 not required
                </Dropdown.Item>
              </ConditionalToolTip>
            )}
            {allowMark1099Required && (
              <ConditionalToolTip
                condition={
                  !season && "Filter by a season to enable this action."
                }
                side="left"
              >
                <Dropdown.Item
                  onClick={markAsRequiredModal.open}
                  disabled={!season}
                >
                  Mark 1099 required
                </Dropdown.Item>
              </ConditionalToolTip>
            )}
            {allowSkipTaxDocument && (
              <ConditionalToolTip
                condition={
                  !season && "Filter by a season to enable this action."
                }
                side="left"
              >
                <Dropdown.Item
                  onClick={notRequiredModal.open}
                  disabled={!season}
                >
                  Mark W-Form not required
                </Dropdown.Item>
              </ConditionalToolTip>
            )}
            {allowDeleteVendor && (
              <Dropdown.Item onClick={deleteModal.open}>
                Delete vendor
              </Dropdown.Item>
            )}
            {vendorName !== expandedRow ? (
              <Dropdown.Item onClick={() => showTransactions(vendorName)}>
                Show transactions
              </Dropdown.Item>
            ) : (
              <Dropdown.Item onClick={() => showTransactions(null)}>
                Hide transactions
              </Dropdown.Item>
            )}
            {!hasEmail && isAdmin && (
              <Dropdown.Item
                onClick={() =>
                  setAnimateVendors((prev) => [...prev, merchantId])
                }
              >
                Request Email Address
              </Dropdown.Item>
            )}

            <Dropdown.Item
              onClick={hasWritePermission ? markAsRelatedParty : undefined}
              disabled={!hasWritePermission}
              tag={!hasWritePermission ? <Padlock /> : undefined}
            >
              {vendor.is_related_party
                ? "Unmark as related party"
                : "Mark as related party"}
            </Dropdown.Item>

            {allowVoidFiling && (
              <Dropdown.Item onClick={markAsVoid.open} type="danger">
                Void filing
              </Dropdown.Item>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <DeleteModal
        show={deleteModal.isOpen}
        closeModal={deleteModal.close}
        onClick={deleteVendor}
        text="Delete vendor"
        body={
          "Are you sure you want to delete this vendor? This action can not be undone."
        }
        isLoading={isDeleting}
      />

      <Modal.Root
        open={notRequiredModal.isOpen}
        onOpenChange={notRequiredModal.close}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title> Mark W-Form not required</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-text-body-lg t-text-text-100">
              Are you sure you want to Mark W-Form not required collection for
              this vendor?
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="t-flex t-justify-end t-gap-3">
              <Modal.RawClose asChild>
                <Button>Cancel</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                isLoading={isEditing}
                disabled={isEditing}
                onClick={markVendorFormNotRequired}
              >
                Confirm
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>

      <MarkAsNotRequired
        {...markAsNotRequiredModal}
        merchantId={merchantId}
        groupId={groupId}
        season={season}
      />
      <MarkAsRequired
        {...markAsRequiredModal}
        merchantId={merchantId}
        groupId={groupId}
        season={season}
      />
      <EditVendorDetails
        vendor={vendor}
        groupId={groupId}
        entityId={entityId}
        selectedSeason={selectedSeason}
        {...editVendorModal}
      />
      {taskId && (
        <MarkVendorAsVoid
          {...markAsVoid}
          merchantId={merchantId}
          groupId={groupId}
          season={season}
          taskId={taskId}
        />
      )}
      {edit1099Modal.isOpen && (
        <Form1099Fields
          {...edit1099Modal}
          wFormMapId={vendor.merchant_data_w_form_map_id}
          form1099Type={vendor.form_1099_type}
          season={season}
          entityId={entityId}
          wFormFile={vendor?.w_form_document}
          groupId={groupId}
          isCorrectedReturn
        />
      )}
    </div>
  );
};
